import { UserPreferenceProvider, useUserPreferenceStore } from '@common/preferences/userPreference';
import { QueryKeys } from '@common/queries/common';
import { Centered } from '@prophecy/ui-v3/Layout';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { parseJSON } from '@prophecy/utils/string';
import { useQuery } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import React, { createContext, useContext, useMemo, useState } from 'react';

import { migrateEntityFabric } from '../data/apis/api';
import { toFabricObject } from '../data/apis/fabricUtils';
import { errorHandler, makeGraphQlCall, useRestQuery } from '../data/util';
import { useGlobalVariables } from './globalConfig/securedConfigHook';
import { useMixPanel } from './mixpanel/context';
import { getUserQuery, UserQueryResponse, UserFabric, User } from './query';
import { UserFabricStatus, UserFabricStatusKnownStatus } from './types';
import { UserSessionContext } from './UserSessionProvider';

export type UserProviderType = {
  user: ReturnType<typeof toUserObject>;
  refetch: ReturnType<typeof useQuery>['refetch'];
};

export const UserContext = createContext<UserProviderType | undefined>(undefined);

export function useUser() {
  return useContext(UserContext) as UserProviderType;
}

export const isProphecyUser = (email: string) => {
  return email.endsWith('@prophecy.io');
};

async function user(userUID: string, isSqlProject: boolean, hasAirflow: boolean) {
  return await fetchUserFabricDocu(userUID, isSqlProject, hasAirflow);
}

/**
 * Hook to get the user session ID from UserSessionContext.
 * @requires UserSessionProvider to be wrapped around the UserProvider
 * @returns The user's session ID
 */
export const useUserSessionId = () => {
  const sessionId = useContext(UserSessionContext);

  if (!sessionId) {
    // This will happen if the UserSessionProvider is not wrapped around the UserProvider
    return 'NO_SESSION_ID';
  }
  return sessionId;
};

export function UserProvider({ children, userUID }: { children: React.ReactNode; userUID: string }) {
  const globalConf = useGlobalVariables();
  const executionUrl = globalConf.getExecutionURL as string;
  const [migrated, setMigrated] = useState(false);

  useRestQuery(['migrateEntityFabric-user'], () => migrateEntityFabric(executionUrl), {
    onError: noop,
    onSettled: () => {
      setMigrated(true);
    },
    retry: false,
    refetchOnWindowFocus: false
  });

  const { identifyAndSetPeople } = useMixPanel();

  const {
    isInitialLoading: isLoading,
    isError,
    data,
    refetch
  } = useQuery(
    [QueryKeys.User],
    user.bind(null, userUID, globalConf.isSQLProjectEnabled, globalConf.isAirflowEnabled),
    {
      onError: errorHandler,
      enabled: migrated
    }
  );

  const { userPreferences, setPreference, isLoading: isUsePreferenceLoading } = useUserPreferenceStore();

  const value = useMemo(
    () =>
      ({
        user: data,
        refetch
      }) as UserProviderType,
    [data, refetch]
  );

  if (isLoading || isUsePreferenceLoading || isError || !data) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }

  if (data?.firstName) {
    const { firstName, lastName, created, company } = data;
    const obj = {
      firstName,
      lastName,
      userId: data.id,
      signup_date: new Date(created).toISOString()
    };
    identifyAndSetPeople(data.email, company ? { ...obj, company: company as string } : obj);
  }
  return (
    <UserContext.Provider value={value}>
      <StableChildren>
        <UserPreferenceProvider preferences={userPreferences} setPreference={setPreference}>
          {children}
        </UserPreferenceProvider>
      </StableChildren>
    </UserContext.Provider>
  );
}
function toUserObject(user: User) {
  const fabrics = user.fabrics.map(toFabricObject);
  const id = user._id;
  const sqlFabricCredsMap =
    parseJSON<{ fabrics: Record<string, boolean> }>(user.SqlFabricCredsInfoAspect?.[0]?.AspectValue, {
      fabrics: {}
    })?.fabrics || {};

  const sqlFabrics: (UserFabric & { provider: string })[] = [];
  const sqlConfiguredFabrics: string[] = [];
  fabrics.forEach((fabric) => {
    if (fabric.SqlProviderInfoAspect?.length) {
      const aspectValueJson = parseJSON(fabric.SqlProviderInfoAspect?.[0]?.AspectValue, {
        providerInfo: { providerType: '' }
      });
      const providerType = aspectValueJson?.providerInfo?.providerType;
      sqlFabrics.push({ ...fabric, provider: providerType });
      if (sqlFabricCredsMap[fabric.id]) {
        sqlConfiguredFabrics.push(fabric.id);
      }
    }
  });

  let sqlFabricStatus: UserFabricStatus = UserFabricStatusKnownStatus.NoFabric;
  if (sqlFabrics.length === 0) {
    sqlFabricStatus = UserFabricStatusKnownStatus.NoFabric;
  } else if (sqlConfiguredFabrics.length > 0) {
    sqlFabricStatus = UserFabricStatusKnownStatus.AlreadyHaveFabric;
  } else {
    sqlFabricStatus = Number(sqlFabrics[0]._id);
  }

  return {
    ...user,
    id,
    fabrics,
    sqlFabrics,
    sqlFabricStatus
  };
}
async function fetchUserFabricDocu(id: string, isSqlProject: boolean, hasAirflow: boolean) {
  const query = getUserQuery(isSqlProject, hasAirflow);
  const { User: user } = await (makeGraphQlCall(query, { id }) as Promise<UserQueryResponse>);
  if (user) {
    return toUserObject(user);
  }
}
